import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Link2, Link2Off, Code as CodeIcon, RemoveFormatting,
} from 'lucide-react'
import type { Block, HeadingBlock, ParagraphBlock, QuoteBlock } from './types'
import { hasRichHtml, inlineRichHtml } from './richtext'

// Inline rich-text editor for heading / paragraph / quote blocks. Renders a
// contentEditable surface that supports inline marks (bold, italic,
// underline, strikethrough, link, inline code, text color) via
// document.execCommand — the same dependency-free approach the notepad
// block uses. A floating toolbar appears whenever the user selects a
// non-empty range, WordPress-style.
//
// Storage model: we keep both `block.text` (plain innerText, for search /
// fallback / {{var}} resolution) and `block.html` (marked-up source). When
// the content carries no real tags we drop `html` so the block stays a
// plain text block and the existing escape-based render path is used.
//
// Var tokens: like the plain editor, while focused we show the RAW source
// (so `{{var}}` is editable verbatim); while blurred we show the resolved
// preview (tokens substituted from inputValues).

type RichTextBlock = HeadingBlock | ParagraphBlock | QuoteBlock

interface Props {
  block: RichTextBlock
  onChange: (next: Block) => void
  onSelect: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, block: Block) => void
  inputValues: Record<string, unknown>
  style?: React.CSSProperties
  placeholder?: string
}

// Raw authoring HTML for the editor surface (tokens kept literal). Prefer
// the marked-up html; otherwise lift plain text into escaped html so the
// caret/format commands operate on a consistent HTML document.
function rawHtml(block: RichTextBlock): string {
  if (hasRichHtml(block.html)) return block.html as string
  // Reuse inlineRichHtml with empty vars to get escaped text + <br>.
  return inlineRichHtml({ text: block.text, html: undefined, vars: {} })
}

export default function InlineRichText({
  block, onChange, onSelect, onKeyDown, inputValues, style, placeholder,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [focused, setFocused] = useState(false)
  const [toolbar, setToolbar] = useState<{ top: number; left: number } | null>(null)
  const [, force] = useState(0)
  const wasFocusedRef = useRef(false)

  // Sync model → DOM. While focused show raw source so formatting commands
  // and {{var}} edits behave; while blurred show the resolved preview.
  //
  // CRITICAL: while focused, the contentEditable itself is the source of
  // truth. We must NOT rewrite innerHTML on every keystroke — typing fires
  // onInput → onChange → re-render → this effect, and assigning innerHTML
  // (even to identical-looking markup) collapses the selection and snaps the
  // caret back to the start. So we only write to the DOM on the blur→focus
  // transition (swap the resolved {{var}} preview for the raw editable
  // source); subsequent edits while focused leave the live DOM untouched.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const wasFocused = wasFocusedRef.current
    wasFocusedRef.current = focused

    if (focused) {
      if (!wasFocused) {
        const next = rawHtml(block)
        if (el.innerHTML !== next) el.innerHTML = next
      }
      return
    }

    const next = inlineRichHtml({ text: block.text, html: block.html, vars: inputValues })
    if (el.innerHTML !== next) el.innerHTML = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.text, block.html, focused, inputValues])

  const pushChange = () => {
    const el = ref.current
    if (!el) return
    const html = el.innerHTML
    const text = el.innerText
    const rich = hasRichHtml(html)
    const nextHtml = rich ? html : undefined
    if ((block.html ?? undefined) === nextHtml && block.text === text) return
    onChange({ ...block, text, html: nextHtml } as Block)
  }

  const updateToolbar = () => {
    force(n => n + 1)
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) { setToolbar(null); return }
    const el = ref.current
    if (!el) return
    const range = sel.getRangeAt(0)
    if (!el.contains(range.commonAncestorContainer)) { setToolbar(null); return }
    const rect = range.getBoundingClientRect()
    if (!rect || (rect.width === 0 && rect.height === 0)) { setToolbar(null); return }
    setToolbar({ top: rect.top - 8, left: rect.left + rect.width / 2 })
  }

  const exec = (command: string, arg?: string) => {
    ref.current?.focus()
    try { document.execCommand(command, false, arg) } catch { /* swallow */ }
    pushChange()
    updateToolbar()
  }

  const isActive = (cmd: string): boolean => {
    try { return document.queryCommandState(cmd) } catch { return false }
  }

  const insertLink = () => {
    const url = window.prompt('Link URL', 'https://')
    if (!url) return
    exec('createLink', url)
  }

  // Wrap the current selection in <code>. execCommand has no inline-code
  // command, so we insert the escaped selected text wrapped in a <code>.
  const inlineCode = () => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return
    const text = sel.toString()
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    exec('insertHTML', `<code>${escaped}</code>`)
  }

  // Keep the toolbar glued to the selection while the page scrolls.
  useLayoutEffect(() => {
    if (!toolbar) return
    const onScroll = () => updateToolbar()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolbar])

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder || ''}
        className="pg-rich-edit"
        onInput={pushChange}
        onFocus={() => { setFocused(true); onSelect() }}
        onBlur={() => { setFocused(false); setToolbar(null); pushChange() }}
        onKeyUp={updateToolbar}
        onMouseUp={updateToolbar}
        onKeyDown={(e) => {
          // Let the format shortcuts fall through to the browser's native
          // contentEditable handling (Ctrl/Cmd+B/I/U), then capture the
          // result. Everything else (Enter / Backspace / "/" slash menu)
          // is delegated to the page-level key handler.
          const mod = e.metaKey || e.ctrlKey
          if (mod && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
            // native handles the toggle; sync model just after.
            window.setTimeout(() => { pushChange(); updateToolbar() }, 0)
            return
          }
          onKeyDown(e, block)
        }}
        onPaste={(e) => {
          // Paste as plain text to keep markup predictable (mirrors notepad).
          e.preventDefault()
          const text = e.clipboardData.getData('text/plain')
          document.execCommand('insertText', false, text)
        }}
        style={{
          outline: 'none',
          minHeight: '1.4em',
          wordBreak: 'break-word',
          ...style,
        }}
      />
      {toolbar && (
        <div
          // Fixed to the viewport so it tracks the selection regardless of
          // the scroll container. Centered above the selection.
          style={{
            position: 'fixed',
            top: toolbar.top,
            left: toolbar.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: 3,
            borderRadius: 7,
            background: 'var(--pg-surface, #1e293b)',
            border: '1px solid var(--pg-border)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <FmtBtn title="Bold (⌘B)" active={isActive('bold')} onClick={() => exec('bold')}><Bold size={14} /></FmtBtn>
          <FmtBtn title="Italic (⌘I)" active={isActive('italic')} onClick={() => exec('italic')}><Italic size={14} /></FmtBtn>
          <FmtBtn title="Underline (⌘U)" active={isActive('underline')} onClick={() => exec('underline')}><UnderlineIcon size={14} /></FmtBtn>
          <FmtBtn title="Strikethrough" active={isActive('strikeThrough')} onClick={() => exec('strikeThrough')}><Strikethrough size={14} /></FmtBtn>
          <Sep />
          <FmtBtn title="Inline code" onClick={inlineCode}><CodeIcon size={14} /></FmtBtn>
          <FmtBtn title="Add link" onClick={insertLink}><Link2 size={14} /></FmtBtn>
          <FmtBtn title="Remove link" onClick={() => exec('unlink')}><Link2Off size={14} /></FmtBtn>
          <Sep />
          <label title="Text color" style={{ ...fmtBtnStyle(false), position: 'relative', padding: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 13, lineHeight: 1 }}>A</span>
            <input
              type="color"
              onChange={(e) => exec('foreColor', e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
          </label>
          <FmtBtn title="Clear formatting" onClick={() => exec('removeFormat')}><RemoveFormatting size={14} /></FmtBtn>
        </div>
      )}
      <style>{`
        .pg-rich-edit:empty:before {
          content: attr(data-placeholder);
          color: var(--pg-text-secondary);
          opacity: 0.55;
          pointer-events: none;
        }
        .pg-rich-edit a { color: var(--pg-accent, var(--pg-primary)); text-decoration: underline; }
        .pg-rich-edit code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 0.88em;
          background: var(--pg-bg);
          border: 1px solid var(--pg-border);
          border-radius: 4px;
          padding: 0 4px;
        }
      `}</style>
    </div>
  )
}

function Sep() {
  return <span style={{ width: 1, height: 16, background: 'var(--pg-border)', margin: '0 2px' }} />
}

function FmtBtn({
  children, title, onClick, active,
}: {
  children: React.ReactNode
  title: string
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active || undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      style={fmtBtnStyle(!!active)}
    >{children}</button>
  )
}

function fmtBtnStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    border: 'none',
    borderRadius: 5,
    background: active ? 'var(--pg-primary, #2563eb)' : 'transparent',
    color: active ? 'var(--pg-on-primary, #fff)' : 'var(--pg-text, #e2e8f0)',
    cursor: 'pointer',
  }
}
